import { Fragment } from "react";
import { Download, Eraser, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono";
const fmt = (n, d = 2) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d });
const csvValue = (v) => String(v ?? "").replace(/,/g, " ");
const compartmentIndexes = [0, 1, 2];

export const MeasurementLog = ({ rows, tankName, setTankName, onAdd, onClear, onDeleteRow, highlightId, isMobileView }) => {
  const totalVolume = rows.reduce((sum, row) => sum + row.volume, 0);

  const exportCsv = () => {
    const header = ["name", "counted_liters", "c1", "c1_product", "c2", "c2_product", "c3", "c3_product", "weight"];
    const lines = [
      header.join(","),
      ...rows.map((r) => {
        const f = r.fillCm || [];
        const p = r.products || [];
        return [
          csvValue(r.name),
          r.volume.toFixed(2),
          f[0] ?? 0,
          csvValue(p[0]),
          f[1] ?? 0,
          csvValue(p[1]),
          f[2] ?? 0,
          csvValue(p[2]),
          r.weight.toFixed(1),
        ].join(",");
      }),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tank-measurements.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const exportVolumeCsv = () => {
    const header = ["name", "counted_liters", "c1", "c1_product", "c2", "c2_product", "c3", "c3_product"];
    const lines = [
      header.join(","),
      ...rows.map((r) => {
        const f = r.fillCm || [];
        const p = r.products || [];
        return [
          csvValue(r.name),
          r.volume.toFixed(2),
          f[0] ?? 0,
          csvValue(p[0]),
          f[1] ?? 0,
          csvValue(p[1]),
          f[2] ?? 0,
          csvValue(p[2]),
        ].join(",");
      }),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tank-measurements-volumes.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"} (volume only) to CSV`);
  };

  return (
    <div className="bg-[#131924] rounded-xl border border-[#243046] p-5 shadow-xl rise rise-2" data-testid="measurement-log-card">
      <div className="flex items-center justify-between">
        <p className={labelCls}>Measurement Log</p>
        <span className="text-[10px] font-mono text-slate-500">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
      </div>

      <div className="flex gap-2 mt-3">
        <Input
          data-testid="tank-name-input"
          placeholder="Tank name (e.g. Depot T1)…"
          value={tankName}
          onChange={(e) => setTankName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onAdd();
          }}
          className="bg-[#0D131E] border-[#243046] font-mono text-slate-100 text-sm focus-visible:ring-amber-500"
        />
        <Button data-testid="add-to-table-btn" onClick={onAdd} className="shrink-0 bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
          <Plus />Add
        </Button>
      </div>

      {rows.length > 0 && (
        <>
          {isMobileView ? (
            <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-[#243046]" data-testid="measurement-log-table">
              {rows.map((r, rowIndex) => {
                const fills = r.fillCm || [];
                const products = r.products || [];
                return (
                  <div
                    key={r.id}
                    className={`border-b border-[#243046]/60 p-3 transition-colors ${r.id === highlightId ? "bg-amber-500/15" : ""}`}
                    data-testid={`log-row-${rowIndex}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 break-words text-sm font-semibold text-slate-200">{r.name}</p>
                      <button title="Remove row" data-testid={`log-delete-row-${rowIndex}`} onClick={() => onDeleteRow(r.id)} className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {compartmentIndexes.map((i) => (
                        <div key={i} className="rounded-lg bg-[#0D131E] p-2">
                          <p className="text-[9px] uppercase tracking-wider text-slate-500">C{i + 1}</p>
                          <p className="text-xs text-slate-300" data-testid={`log-row-${rowIndex}-c${i + 1}`}>{fmt(fills[i] ?? 0)}</p>
                          <p className="mt-1 text-[9px] uppercase tracking-wider text-slate-500">Product {i + 1}</p>
                          <p className="text-xs text-slate-300 break-words" data-testid={`log-row-${rowIndex}-product-${i + 1}`}>{products[i] || "—"}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between gap-3 mt-3">
                      <div>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500">Volume</p>
                        <p className="text-sm text-amber-400 font-semibold" data-testid={`log-row-${rowIndex}-volume`}>{fmt(r.volume)} L</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] uppercase tracking-wider text-slate-500">Weight</p>
                        <p className="text-sm text-slate-300" data-testid={`log-row-${rowIndex}-weight`}>{fmt(r.weight, 1)} kg</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="sticky bottom-0 flex items-center justify-between border-t border-amber-500/30 bg-[#0D131E] px-3 py-2" data-testid="log-total-row">
                <span className="text-xs font-mono font-semibold text-slate-200">Total Volume</span>
                <span className="text-amber-400 font-semibold" data-testid="log-total-volume">{fmt(totalVolume)} L</span>
              </div>
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto max-h-64 overflow-y-auto rounded-lg border border-[#243046]">
              <table className="w-full min-w-[760px] text-xs font-mono" data-testid="measurement-log-table">
                <thead className="sticky top-0 z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-[#0D131E] border-b border-[#243046]">
                    <th className="py-1.5 px-3">Tank Name</th>
                    {compartmentIndexes.map((i) => (
                      <Fragment key={i}>
                        <th className="py-1.5 px-3">C{i + 1}</th>
                        <th className="py-1.5 px-3">Product {i + 1}</th>
                      </Fragment>
                    ))}
                    <th className="py-1.5 px-3">Volume</th>
                    <th className="py-1.5 px-3">Weight</th>
                    <th className="py-1.5 px-2 w-9" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, rowIndex) => {
                    const fills = r.fillCm || [];
                    const products = r.products || [];
                    return (
                      <tr
                        key={r.id}
                        className={`border-b border-[#243046]/60 transition-colors ${r.id === highlightId ? "bg-amber-500/15" : ""}`}
                        data-testid={`log-row-${rowIndex}`}
                      >
                        <td className="py-2 px-3 text-slate-200">{r.name}</td>
                        {compartmentIndexes.map((i) => (
                          <Fragment key={i}>
                            <td className="py-2 px-3 text-slate-400" data-testid={`log-row-${rowIndex}-c${i + 1}`}>{fmt(fills[i] ?? 0)}</td>
                            <td className="py-2 px-3 text-slate-400" data-testid={`log-row-${rowIndex}-product-${i + 1}`}>{products[i] || "—"}</td>
                          </Fragment>
                        ))}
                        <td className="py-2 px-3 text-amber-400" data-testid={`log-row-${rowIndex}-volume`}>{fmt(r.volume)} L</td>
                        <td className="py-2 px-3 text-slate-300" data-testid={`log-row-${rowIndex}-weight`}>{fmt(r.weight, 1)} kg</td>
                        <td className="py-2 px-2">
                          <button title="Remove row" data-testid={`log-delete-row-${rowIndex}`} onClick={() => onDeleteRow(r.id)} className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0">
                  <tr className="bg-[#0D131E] border-t border-amber-500/30" data-testid="log-total-row">
                    <td className="py-2 px-3 text-xs font-mono font-semibold text-slate-200" colSpan="7">Total Volume</td>
                    <td className="py-2 px-3 text-amber-400 font-semibold" data-testid="log-total-volume">{fmt(totalVolume)} L</td>
                    <td colSpan="2" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <Button data-testid="export-log-csv-btn" onClick={exportCsv} variant="outline" className="border-[#243046] bg-transparent text-slate-200 hover:bg-[#1A2232] hover:text-slate-100">
              <Download />Export CSV
            </Button>
            <Button data-testid="export-log-volume-csv-btn" onClick={exportVolumeCsv} variant="outline" className="border-[#243046] bg-transparent text-slate-200 hover:bg-[#1A2232] hover:text-slate-100">
              <Download />Export Volume CSV
            </Button>
            <Button
              data-testid="clear-table-btn"
              onClick={() => {
                onClear();
                toast.success("Measurement log cleared");
              }}
              variant="outline"
              className="border-[#243046] bg-transparent text-slate-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/40"
            >
              <Eraser />Clear Table
            </Button>
          </div>
        </>
      )}
      {rows.length === 0 && (
        <p className="text-[11px] text-slate-500 font-mono mt-2" data-testid="log-empty">No entries yet — set a tank name and hit Add to log the current reading.</p>
      )}
    </div>
  );
};
